import type { WorkflowRun } from '../../api/workflowRuns'
import type { DynamicIslandActivity, DynamicIslandAccent } from '../../context/dynamicIslandTypes'
import { workflowRunProgressPercent } from '../workflow/workflowRunArtifacts'
import { isWorkflowRunActive, workflowRunStatusLabel } from '../workflow/workflowRunDisplay'

export type WorkflowRunIslandHandlers = {
  onOpenDetail: () => void
  onClosePanel?: () => void
  detailTitle?: string
  renderDetail?: () => import('react').ReactNode
}

function currentStepLabel(run: WorkflowRun): string {
  const running = run.steps.find((step) => step.status === 'running')
  if (running) return running.label
  const latest = [...run.steps].reverse().find((step) => step.status !== 'queued')
  return latest?.label ?? '执行中'
}

function workflowStatusAccent(status: string): DynamicIslandAccent {
  const key = status.trim().toLowerCase()
  if (key === 'failed') return 'danger'
  if (key === 'succeeded') return 'success'
  if (key === 'cancelled') return 'cancelled'
  if (key === 'running' || key === 'awaiting_input') return 'warn'
  return 'muted'
}

export function mapWorkflowRunToActivity(
  run: WorkflowRun,
  handlers: WorkflowRunIslandHandlers,
): DynamicIslandActivity {
  const progress = workflowRunProgressPercent(run)
  const statusLabel = workflowRunStatusLabel(run.status)
  const stepLabel = currentStepLabel(run)
  const accent = workflowStatusAccent(run.status)
  const terminal = !isWorkflowRunActive(run.status)
  const title = stepLabel
  const expandedTitle = `工作流 · ${stepLabel}`
  const subtitle = statusLabel

  const actions: DynamicIslandActivity['actions'] = [
    {
      id: 'detail',
      label: '详情',
      onClick: handlers.onOpenDetail,
    },
    {
      id: 'close',
      label: '关闭',
      onClick: handlers.onClosePanel ?? (() => {}),
    },
  ]

  if (terminal) {
    return {
      id: `workflow-run:${run.publicId}`,
      sourceKind: 'workflow.run',
      sourceLabel: '工作流',
      priority: 12,
      lifecycle: 'terminal',
      compact: {
        mode: 'terminal',
        title,
        hint: subtitle,
        accent,
      },
      expanded: {
        mode: 'summary',
        title: expandedTitle,
        description: `运行 ${run.publicId} · ${subtitle}`,
        progressPercent: progress,
        accent,
      },
      actions,
      detailTitle: handlers.detailTitle ?? expandedTitle,
      renderDetail: handlers.renderDetail,
    }
  }

  return {
    id: `workflow-run:${run.publicId}`,
    sourceKind: 'workflow.run',
    sourceLabel: '工作流',
    priority: 12,
    lifecycle: 'active',
    compact: {
      mode: 'step',
      title,
      stepLabel,
      subtitle,
      progressPercent: progress,
      accent,
      statusLabel,
    },
    expanded: {
      mode: 'summary',
      title: expandedTitle,
      description: `运行 ${run.publicId} · ${subtitle}`,
      progressPercent: progress,
      accent,
      statusLabel,
    },
    actions,
    detailTitle: handlers.detailTitle ?? expandedTitle,
    renderDetail: handlers.renderDetail,
  }
}
